import Graph from 'graphology';

import {
  computeLayoutParams,
  NODE_POSITIONING,
} from '@/components/nodeGraph/config/graphConfig';

// Re-export computeLayoutParams from config for backward compatibility
export { computeLayoutParams };

// Helper function to check if a point is valid (not too close to existing points)
const isValidPoint = (
  newX: number,
  newY: number,
  positions: { x: number; y: number }[],
  minDistance: number,
  width: number,
  height: number
): boolean => {
  // Check bounds
  if (
    newX < -width / 2 ||
    newX > width / 2 ||
    newY < -height / 2 ||
    newY > height / 2
  ) {
    return false;
  }

  // Check if point is too close to existing points
  for (const pos of positions) {
    const distance = Math.sqrt((newX - pos.x) ** 2 + (newY - pos.y) ** 2);
    if (distance < minDistance) {
      return false;
    }
  }

  return true;
};

// Helper function to generate a random point around a given point
const generateRandomPointAround = (
  centerX: number,
  centerY: number,
  minDistance: number
): { x: number; y: number } => {
  const angle = Math.random() * 2 * Math.PI;
  const radius = minDistance + Math.random() * minDistance;
  return {
    x: centerX + Math.cos(angle) * radius,
    y: centerY + Math.sin(angle) * radius,
  };
};

// Helper function to fill remaining positions with random points
const fillRemainingPositions = (
  positions: { x: number; y: number }[],
  nodeCount: number,
  width: number,
  height: number,
  minDistance: number
): void => {
  while (positions.length < nodeCount) {
    let attempts = 0;
    let validPosition = false;

    while (!validPosition && attempts < 100) {
      const x = Math.random() * width - width / 2;
      const y = Math.random() * height - height / 2;

      if (isValidPoint(x, y, positions, minDistance * 0.7, width, height)) {
        positions.push({ x, y });
        validPosition = true;
      }
      attempts++;
    }

    if (!validPosition) {
      // Last resort: just place randomly
      positions.push({
        x: Math.random() * width - width / 2,
        y: Math.random() * height - height / 2,
      });
    }
  }
};

// Uniform distribution using Poisson disk sampling for even spacing
export const generateUniformPositions = (
  nodeCount: number,
  width: number = NODE_POSITIONING.asset.width,
  height: number = NODE_POSITIONING.asset.height,
  minDistance: number = NODE_POSITIONING.asset.minDistance
): { x: number; y: number }[] => {
  const positions: { x: number; y: number }[] = [];

  // Add first point
  const firstX = Math.random() * width - width / 2;
  const firstY = Math.random() * height - height / 2;
  positions.push({ x: firstX, y: firstY });

  const activeList: { x: number; y: number; index: number }[] = [
    { x: firstX, y: firstY, index: 0 },
  ];

  // Generate points using Poisson disk sampling
  while (activeList.length > 0 && positions.length < nodeCount) {
    const randomIndex = Math.floor(Math.random() * activeList.length);
    const currentPoint = activeList[randomIndex];
    let found = false;

    // Try to place up to 30 points around current point
    for (let i = 0; i < 30 && positions.length < nodeCount; i++) {
      const newPoint = generateRandomPointAround(
        currentPoint.x,
        currentPoint.y,
        minDistance
      );

      if (
        isValidPoint(
          newPoint.x,
          newPoint.y,
          positions,
          minDistance,
          width,
          height
        )
      ) {
        positions.push(newPoint);
        activeList.push({
          x: newPoint.x,
          y: newPoint.y,
          index: positions.length - 1,
        });
        found = true;
      }
    }

    if (!found) {
      activeList.splice(randomIndex, 1);
    }
  }

  // Fill remaining positions if needed
  fillRemainingPositions(positions, nodeCount, width, height, minDistance);

  return positions;
};

export const getNodePosition = ({
  graph: _graph,
  nodeId,
  existingPositions,
  initialRadiusMultiplier: _initialRadiusMultiplier = 2.0,
  spreadRangeMultiplier: _spreadRangeMultiplier = 1.0,
  parentX = 0,
  parentY = 0,
}: {
  graph: Graph;
  nodeId: string;
  existingPositions: Map<string, { x: number; y: number }>;
  initialRadiusMultiplier?: number;
  spreadRangeMultiplier?: number;
  parentX?: number;
  parentY?: number;
}) => {
  let x, y;
  if (existingPositions.has(nodeId)) {
    const pos = existingPositions.get(nodeId)!;
    x = pos.x;
    y = pos.y;
  } else if (nodeId.startsWith('#asset#')) {
    x =
      Math.random() * NODE_POSITIONING.asset.width -
      NODE_POSITIONING.asset.width / 2;
    y =
      Math.random() * NODE_POSITIONING.asset.height -
      NODE_POSITIONING.asset.height / 2;
  } else if (nodeId.startsWith('#attribute#')) {
    const angle = Math.random() * 2 * Math.PI;
    const radius =
      NODE_POSITIONING.attribute.orbitRadius +
      Math.random() * NODE_POSITIONING.attribute.orbitVariation;
    x = parentX + Math.cos(angle) * radius;
    y = parentY + Math.sin(angle) * radius;
  } else {
    const angle = Math.random() * 2 * Math.PI;
    const radius =
      NODE_POSITIONING.other.orbitRadius +
      Math.random() * NODE_POSITIONING.other.orbitVariation;
    x = parentX + Math.cos(angle) * radius;
    y = parentY + Math.sin(angle) * radius;
  }
  existingPositions.set(nodeId, { x, y });

  return { x, y };
};

// Add convex hull calculation function
export const calculateConvexHull = (
  points: { x: number; y: number }[]
): { x: number; y: number }[] => {
  if (points.length < 3) return points;

  // Graham Scan algorithm
  const pivot = points.reduce((lowest, point) =>
    point.y < lowest.y || (point.y === lowest.y && point.x < lowest.x)
      ? point
      : lowest
  );

  const sorted = points
    .filter(p => p !== pivot)
    .sort((a, b) => {
      const angleA = Math.atan2(a.y - pivot.y, a.x - pivot.x);
      const angleB = Math.atan2(b.y - pivot.y, b.x - pivot.x);
      return angleA - angleB;
    });

  const hull = [pivot];

  for (const point of sorted) {
    while (hull.length >= 2) {
      const last = hull[hull.length - 1];
      const secondLast = hull[hull.length - 2];

      const cross =
        (last.x - secondLast.x) * (point.y - secondLast.y) -
        (last.y - secondLast.y) * (point.x - secondLast.x);

      if (cross > 0) break;
      hull.pop();
    }
    hull.push(point);
  }

  return hull;
};

// Add helper function to smooth points
export const smoothPoints = (
  points: { x: number; y: number }[],
  tension: number = 0.5
): { x: number; y: number }[] => {
  if (points.length < 3) return points;

  const smoothed: { x: number; y: number }[] = [];
  const len = points.length;

  // Helper to get point with wrap-around
  const getPoint = (i: number) => points[(i + len) % len];

  for (let i = 0; i < len; i++) {
    const p0 = getPoint(i - 1);
    const p1 = getPoint(i);
    const p2 = getPoint(i + 1);

    // Calculate control points
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;

    smoothed.push({ x: p1.x, y: p1.y });
    smoothed.push({ x: cp1x, y: cp1y });
  }

  return smoothed;
};

// Add color generation function
export const generateClusterColors = (count: number): string[] => {
  const colors: string[] = [];
  const hueStep = 360 / count;

  for (let i = 0; i < count; i++) {
    const hue = (i * hueStep) % 360;
    colors.push(`hsla(${hue}, 70%, 50%, 0.2)`);
  }

  return colors;
};

// Add helper function to add padding points
export const addPaddingPoints = (
  points: { x: number; y: number }[],
  padding: number
): { x: number; y: number }[] => {
  if (points.length < 2) return points;

  const paddedPoints: { x: number; y: number }[] = [];

  // For each point, add padding points in a circle around it
  points.forEach(point => {
    for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
      paddedPoints.push({
        x: point.x + Math.cos(angle) * padding,
        y: point.y + Math.sin(angle) * padding,
      });
    }
  });

  return paddedPoints;
};

/**
 * Creates weight-based positioning for nodes based on their attribute weights
 * Nodes with similar weights are positioned closer together
 */
export const generateWeightBasedPositions = (
  graph: Graph,
  activeWeight: { name: string; weight: number } | undefined,
  width: number = 4000,
  height: number = 3000,
  minDistance: number = 100
): Map<string, { x: number; y: number }> => {
  const positions = new Map<string, { x: number; y: number }>();

  if (!activeWeight || activeWeight.weight !== 20) {
    // If no active weight, use uniform distribution
    const uniformPositions = generateUniformPositions(
      graph.order,
      width,
      height,
      minDistance
    );
    let index = 0;
    graph.forEachNode(nodeId => {
      if (index < uniformPositions.length) {
        positions.set(nodeId, uniformPositions[index]);
        index++;
      }
    });
    return positions;
  }

  // Group nodes by their attribute values for the active weight
  const clusters = new Map<string, string[]>();
  const unclusteredNodes: string[] = [];

  graph.forEachNode(nodeId => {
    // Check if this node has an edge to an attribute node with the active weight
    let clustered = false;
    graph.forEachOutEdge(nodeId, (_edgeId, _attributes, _source, target) => {
      if (target.startsWith('#attribute#')) {
        const label = graph.getNodeAttribute(target, 'label');
        const field = label.split(':')[0];

        if (field === activeWeight.name) {
          const value = label.split(':')[1]?.trim() || 'Unknown';
          if (!clusters.has(value)) {
            clusters.set(value, []);
          }
          clusters.get(value)!.push(nodeId);
          clustered = true;
        }
      }
    });

    if (!clustered) {
      unclusteredNodes.push(nodeId);
    }
  });

  // Calculate cluster positions in a grid-like pattern
  const clusterValues = Array.from(clusters.keys());
  const numClusters = clusterValues.length;

  if (numClusters === 0) {
    // No clusters, use uniform distribution
    const uniformPositions = generateUniformPositions(
      graph.order,
      width,
      height,
      minDistance
    );
    let index = 0;
    graph.forEachNode(nodeId => {
      if (index < uniformPositions.length) {
        positions.set(nodeId, uniformPositions[index]);
        index++;
      }
    });
    return positions;
  }

  // Arrange clusters in a roughly circular pattern
  const centerX = 0;
  const centerY = 0;
  const clusterRadius = Math.min(width, height) * 0.3; // Radius for cluster centers

  clusterValues.forEach((value, clusterIndex) => {
    const nodes = clusters.get(value)!;

    // Calculate cluster center position
    const angle = (clusterIndex / numClusters) * 2 * Math.PI;
    const clusterCenterX = centerX + Math.cos(angle) * clusterRadius;
    const clusterCenterY = centerY + Math.sin(angle) * clusterRadius;

    // Position nodes within the cluster using a smaller circular distribution
    const nodeRadius = Math.min(100, clusterRadius * 0.3); // Radius for nodes within cluster

    nodes.forEach((nodeId, nodeIndex) => {
      if (nodes.length === 1) {
        // Single node in cluster, place at cluster center
        positions.set(nodeId, { x: clusterCenterX, y: clusterCenterY });
      } else {
        // Multiple nodes, arrange in circle around cluster center
        const nodeAngle = (nodeIndex / nodes.length) * 2 * Math.PI;
        const nodeX = clusterCenterX + Math.cos(nodeAngle) * nodeRadius;
        const nodeY = clusterCenterY + Math.sin(nodeAngle) * nodeRadius;
        positions.set(nodeId, { x: nodeX, y: nodeY });
      }
    });
  });

  // Position unclustered nodes around the periphery
  if (unclusteredNodes.length > 0) {
    const peripheryRadius = Math.min(width, height) * 0.45;
    unclusteredNodes.forEach((nodeId, index) => {
      const angle = (index / unclusteredNodes.length) * 2 * Math.PI;
      const x = centerX + Math.cos(angle) * peripheryRadius;
      const y = centerY + Math.sin(angle) * peripheryRadius;
      positions.set(nodeId, { x, y });
    });
  }

  return positions;
};
